import "@web-components/cart";
import "@web-components/drawer";
import "@web-components/accordion";
import "@web-components/quantity-input";

import { Effect, Queue, Option, Fiber, Ref, Duration } from "effect";
import { CartRuntime } from "@/lib/effect/cart";
import { AddToCartEvents, type AddToCartEvent } from "@/lib/effect/cart";

const onLatestSuccess = (e: Extract<AddToCartEvent, { _tag: "success" }>) =>
  Effect.sync(() => {
    const sidecart = document.getElementById("ajaxCartDrawer");
    if (!sidecart) return;
    sidecart.setAttribute("is-open", "true");
  });

export const startDebouncedLatestSuccessListener = (
  q: Queue.Dequeue<AddToCartEvent>,
  millis = 150,
) =>
  Effect.scoped(
    Effect.gen(function* () {
      const latestSuccessRef = yield* Ref.make<
        Option.Option<Extract<AddToCartEvent, { _tag: "success" }>>
      >(Option.none());

      // holds the currently scheduled debounce fiber (if any)
      const debounceFiberRef = yield* Ref.make<
        Option.Option<Fiber.RuntimeFiber<void>>
      >(Option.none());

      const scheduleDebouncedFire = Effect.gen(function* () {
        // cancel prior timer
        const prev = yield* Ref.get(debounceFiberRef);
        if (Option.isSome(prev)) {
          yield* Fiber.interrupt(prev.value);
        }

        // start a new timer
        const f = yield* Effect.fork(
          Effect.sleep(Duration.millis(millis)).pipe(
            Effect.zipRight(
              Ref.get(latestSuccessRef).pipe(
                Effect.flatMap(
                  Option.match({
                    onNone: () => Effect.void,
                    onSome: onLatestSuccess,
                  }),
                ),
              ),
            ),
          ),
        );

        yield* Ref.set(debounceFiberRef, Option.some(f));
      });

      yield* Queue.take(q).pipe(
        Effect.flatMap((ev) => {
          if (ev._tag === "success") {
            return Ref.set(latestSuccessRef, Option.some(ev)).pipe(
              Effect.zipRight(scheduleDebouncedFire),
            );
          }
          return Effect.void;
        }),
        Effect.forever,
      );
    }),
  );

const addDrawerTriggers = Effect.fn("sidecart/addDrawerTriggers")(function* () {
  const sidecart = document.getElementById("ajaxCartDrawer");
  if (!sidecart) return Effect.void;
  const cartLinks = yield* Effect.sync(() =>
    Array.from(
      document.querySelectorAll(
        `a[href="${window.BrytDesigns.routes.cart_url.replace(".js", "")}"]`,
      ),
    ),
  );
  yield* Effect.forEach(
    cartLinks,
    (link) =>
      Effect.sync(() => {
        const linkClone = link.cloneNode(true);
        const drawerTrigger = document.createElement("drawer-trigger");
        drawerTrigger.appendChild(linkClone);
        drawerTrigger.setAttribute("target", "#ajaxCartDrawer");
        drawerTrigger.setAttribute("on", "click");
        drawerTrigger.setAttribute("prevent-default", "true");
        drawerTrigger.setAttribute("action", "toggle");
        link.replaceWith(drawerTrigger);
      }),
    {
      concurrency: 5,
    },
  );
});

const main = Effect.gen(function* () {
  const events = yield* AddToCartEvents;
  const sub = yield* events.subscribe;
  yield* Effect.fork(startDebouncedLatestSuccessListener(sub, 1000));
  yield* addDrawerTriggers();
  yield* Effect.never;
}).pipe(Effect.scoped);

document.addEventListener("DOMContentLoaded", () => {
  CartRuntime.runFork(main);
});
